import {
  EnrichedSupplierForDelivery,
  SupplierPartForDelivery,
} from "../types";

export interface RouteRemovedSplitPartInput {
  splits: EnrichedSupplierForDelivery[];
  sourceSplitId: string;
  partIndex: number;
  confirmationNeededSupplierId?: string | null;
  additionalTimeRequiredSupplierId?: string | null;
}

export interface UnassignedPartQty {
  partId?: string;
  partNumber: string;
  partName: string;
  quantity: number;
  mrp?: number;
  /** Supplier the part was removed from (for selection-bucket qty restore). */
  sourceSupplierId: string;
}

export interface RouteRemovedSplitPartResult {
  splits: EnrichedSupplierForDelivery[];
  /** Set when the part left the estimate (back to Remaining Parts bucket). */
  unassigned?: UnassignedPartQty;
}

function normalizeId(id: string | null | undefined): string {
  return String(id ?? "").trim();
}

function isSpecialSupplierId(
  supplierId: string | null | undefined,
  confirmationId: string,
  timeRequiredId: string,
): boolean {
  const id = normalizeId(supplierId);
  if (!id) return false;
  return (
    (Boolean(confirmationId) && id === confirmationId) ||
    (Boolean(timeRequiredId) && id === timeRequiredId)
  );
}

function partKey(part: SupplierPartForDelivery): string {
  if (part.partId) return `id:${part.partId}`;
  return `pn:${part.partNumber}`;
}

function mergePartIntoSplit(
  split: EnrichedSupplierForDelivery,
  part: SupplierPartForDelivery,
): EnrichedSupplierForDelivery {
  const key = partKey(part);
  const existingIndex = split.parts.findIndex((p) => partKey(p) === key);
  if (existingIndex < 0) {
    return {
      ...split,
      parts: [...split.parts, { ...part }],
    };
  }
  const nextParts = split.parts.map((p, index) =>
    index === existingIndex
      ? { ...p, quantity: p.quantity + part.quantity }
      : p,
  );
  return { ...split, parts: nextParts };
}

/**
 * Removes a part row from a split and routes it to a special supplier (with qty)
 * or unassigns it back to Smart Supplier Selection Remaining Parts.
 * Never creates/grows a Supplier Not Found (null supplier) split.
 */
export function routeRemovedSplitPart(
  input: RouteRemovedSplitPartInput,
): RouteRemovedSplitPartResult {
  const confirmationId = normalizeId(input.confirmationNeededSupplierId);
  const timeRequiredId = normalizeId(input.additionalTimeRequiredSupplierId);

  const sourceIndex = input.splits.findIndex(
    (s) => s.splitId === input.sourceSplitId,
  );
  if (sourceIndex < 0) {
    return { splits: input.splits };
  }

  const source = input.splits[sourceIndex];
  if (
    input.partIndex < 0 ||
    input.partIndex >= source.parts.length ||
    source.supplierId == null
  ) {
    return { splits: input.splits };
  }

  const removedPart = { ...source.parts[input.partIndex] };
  if (!removedPart.quantity || removedPart.quantity <= 0) {
    return { splits: input.splits };
  }

  let nextSplits = input.splits.map((split, index) => {
    if (index !== sourceIndex) return split;
    return {
      ...split,
      parts: split.parts.filter((_, i) => i !== input.partIndex),
    };
  });

  const unassign = (): RouteRemovedSplitPartResult => {
    nextSplits = nextSplits.filter(
      (s) => s.parts.length > 0 || s.supplierId == null,
    );
    // Drop empty real-supplier splits; also drop empty remaining if empty
    nextSplits = nextSplits.filter((s) => s.parts.length > 0);
    return {
      splits: nextSplits,
      unassigned: {
        partId: removedPart.partId,
        partNumber: removedPart.partNumber,
        partName: removedPart.partName,
        quantity: removedPart.quantity,
        mrp: removedPart.mrp,
        sourceSupplierId: String(source.supplierId),
      },
    };
  };

  if (isSpecialSupplierId(source.supplierId, confirmationId, timeRequiredId)) {
    return unassign();
  }

  const specialSplitsPresent = nextSplits.filter((s) =>
    isSpecialSupplierId(s.supplierId, confirmationId, timeRequiredId),
  );

  if (specialSplitsPresent.length !== 1) {
    return unassign();
  }

  const destinationSplitId = specialSplitsPresent[0].splitId;
  nextSplits = nextSplits.map((split) =>
    split.splitId === destinationSplitId
      ? mergePartIntoSplit(split, removedPart)
      : split,
  );
  nextSplits = nextSplits.filter((s) => s.parts.length > 0);

  return { splits: nextSplits };
}
